"use client";

import { useMemo, useState, useCallback, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { LanguageSelector } from "@/components/ui/language-selector";
import { useTranslation } from "react-i18next";
import { ChatIcon, SearchIcon, PortfolioIcon, UserIcon, SideNavigationIcon } from "@/components/icons";

export function DesktopSidebar() {
  const { t } = useTranslation();
  const pathname = usePathname() ?? '';
  const { user, signOut } = useAuth();
  const isAuthenticatedUser = !!user;

  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = isPinned || isHovered;

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', isExpanded ? '16rem' : '5rem');
  }, [isExpanded]);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  const togglePin = () => setIsPinned(prev => !prev);

  type SocialStatus = "success" | "partial_success" | "in_progress" | "error" | null;
  const [socialStatus, setSocialStatus] = useState<SocialStatus>(null);

  const normalizeStatus = useCallback((raw: any): SocialStatus => {
    const s = (raw || '').toString().trim().toLowerCase();
    if (!s) return null;
    if (["success", "succeeded", "completed", "done"].includes(s)) return "success";
    if (["partial_success", "partial", "partialsuccess"].includes(s)) return "partial_success";
    if (["in_progress", "processing", "running", "queued", "pending"].includes(s)) return "in_progress";
    if (["error", "failed", "failure", "canceled", "cancelled"].includes(s)) return "error";
    return "error";
  }, []);

  const [socialConnectionIds, setSocialConnectionIds] = useState<{instagram?: string, tiktok?: string}>({});

  useEffect(() => {
    if (!isAuthenticatedUser) {
      setSocialStatus(null);
      return;
    }
    let aborted = false;
    const load = async () => {
      try {
        const resp = await fetch('/api/social/accounts', { headers: { 'Accept': 'application/json' } });
        if (aborted) return;
        const json = await resp.json();
        if (!resp.ok || !json?.success) {
          setSocialConnectionIds({});
          setSocialStatus('error');
          return;
        }
        const accounts: any[] = Array.isArray(json.accounts) ? json.accounts : [];
        const igConnIds = accounts.filter((a: any) => a?.connectionType === 'instagram_direct' || a?.connectionType === 'instagram_via_facebook').map((a: any) => a.connectionId).filter(Boolean);
        const tiktokConnIds = accounts.filter((a: any) => a?.connectionType === 'tiktok' || a?.connectionType === 'tiktok_business').map((a: any) => a.connectionId).filter(Boolean);
        const connectionIds = {
          instagram: igConnIds.length ? igConnIds.sort().join(',') : undefined,
          tiktok: tiktokConnIds.length ? tiktokConnIds.sort().join(',') : undefined
        };
        setSocialConnectionIds(connectionIds);
        if (!connectionIds.instagram && !connectionIds.tiktok) {
          setSocialStatus('error');
          return;
        }

        setSocialStatus('in_progress');

        try {
          const statusPromises: Promise<SocialStatus>[] = [];
          if (connectionIds.instagram) {
            statusPromises.push(
              fetch(`/api/instagram/social-fetch-status?connectionIds=${encodeURIComponent(connectionIds.instagram)}`, { cache: 'no-store', headers: { 'Accept': 'application/json' } } as RequestInit)
                .then(r => r.json())
                .then(j => normalizeStatus(j?.status))
                .catch(() => 'error' as SocialStatus)
            );
          }
          if (connectionIds.tiktok) {
            statusPromises.push(
              fetch(`/api/tiktok/social-fetch-status?connectionIds=${encodeURIComponent(connectionIds.tiktok)}`, { cache: 'no-store', headers: { 'Accept': 'application/json' } } as RequestInit)
                .then(r => r.json())
                .then(j => normalizeStatus(j?.status))
                .catch(() => 'error' as SocialStatus)
            );
          }

          const statuses = await Promise.all(statusPromises);
          if (aborted) return;

          let nextStatus: SocialStatus = null;
          if (statuses.some(s => s === 'error')) {
            nextStatus = statuses.some(s => s === 'success' || s === 'partial_success') ? 'partial_success' : 'error';
          } else if (statuses.some(s => s === 'in_progress')) {
            nextStatus = statuses.some(s => s === 'success' || s === 'partial_success') ? 'partial_success' : 'in_progress';
          } else if (statuses.some(s => s === 'partial_success')) {
            nextStatus = 'partial_success';
          } else if (statuses.some(s => s === 'success')) {
            nextStatus = 'success';
          } else {
            nextStatus = 'error';
          }

          setSocialStatus(nextStatus);
        } catch {
          if (!aborted) setSocialStatus('error');
        }
      } catch {
        if (!aborted) setSocialStatus('error');
      }
    };
    load();
    return () => { aborted = true; };
  }, [user, isAuthenticatedUser]);

  useEffect(() => {
    const onTriggered = () => setSocialStatus('in_progress');
    const onCompleted = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail || {};
        const normalized: SocialStatus = normalizeStatus(detail.status);
        setSocialStatus(normalized);
      } catch {}
    };
    window.addEventListener('ig-index-triggered', onTriggered);
    window.addEventListener('ig-index-completed', onCompleted);
    window.addEventListener('tiktok-index-triggered', onTriggered);
    window.addEventListener('tiktok-index-completed', onCompleted);
    return () => {
      window.removeEventListener('ig-index-triggered', onTriggered);
      window.removeEventListener('ig-index-completed', onCompleted);
      window.removeEventListener('tiktok-index-triggered', onTriggered);
      window.removeEventListener('tiktok-index-completed', onCompleted);
    };
  }, [normalizeStatus]);

  const socialDot = useMemo(() => {
    const color =
      socialStatus === 'success' ? 'bg-green-500' :
      socialStatus === 'partial_success' ? 'bg-yellow-500' :
      socialStatus === 'in_progress' ? 'bg-neutral-400 animate-pulse' :
      socialStatus === 'error' ? 'bg-red-500' : 'bg-neutral-400 animate-pulse';
    const title =
      socialStatus === 'success' ? t('nav.dataStatus.connected') :
      socialStatus === 'partial_success' ? t('nav.dataStatus.needsUpdate') :
      socialStatus === 'in_progress' ? t('nav.dataStatus.loading') :
      socialStatus === 'error' ? t('nav.dataStatus.notConnected') :
      t('nav.dataStatus.checking');
    return (
      <div className={`flex items-center gap-2 w-full ${!isExpanded ? 'justify-center' : ''}`} title={title} aria-label={title}>
        <span className={`inline-block h-2 w-2 rounded-full ${color} flex-shrink-0`} />
        <span className={`text-sm text-muted-foreground ${!isExpanded ? 'hidden' : ''}`}>
          {socialStatus === 'error' ? t('nav.dataStatus.labelNotConnected') : title}
        </span>
      </div>
    );
  }, [socialStatus, isExpanded]);

  const navLinks = [
    { href: "/chat", label: t('nav.chat'), icon: ChatIcon },
    // { href: "/search", label: t('nav.search'), icon: SearchIcon },
    // { href: "/portfolio", label: t('nav.portfolio'), icon: PortfolioIcon },
    { href: "/account", label: t('nav.account'), icon: UserIcon },
  ];

  return (
    <div
      className={`hidden md:flex md:sticky md:top-0 md:overflow-y-auto h-screen flex-shrink-0 flex-col border-r border-border bg-background transition-all duration-200 ${isExpanded ? 'w-40' : 'w-20'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex h-16 items-center justify-center">
        <div className={isExpanded ? 'w-full max-w-[calc(100%-2rem)] mx-auto' : ''}>
          <Link href="/" className={`flex items-center gap-2 font-semibold ${!isExpanded ? 'justify-center' : ''}`}>
            <Image src="/favicon.ico" alt="Lema Logo" className="h-9 w-9" width={36} height={36} />
            <span className={`${!isExpanded ? 'hidden' : ''}`}><h1 className="text-3xl font-normal">Lema</h1></span>
          </Link>
        </div>
      </div>
      <nav className="flex-1 space-y-2 py-4">
        <div className={`flex flex-col ${isExpanded ? 'w-full mx-auto' : ''} gap-3`}>
          {navLinks.map(link => {
            const Icon = link.icon;
            const iconClassName = link.href === "/search"
              ? "h-6 w-6 flex-shrink-0"
              : "h-5 w-5 flex-shrink-0";
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`flex items-center gap-3 rounded-lg p-3 transition-all ${
                  pathname.startsWith(link.href)
                    ? "text-primary bg-muted"
                    : "text-muted-foreground hover:text-primary hover:bg-neutral-400"
                } ${!isExpanded ? 'justify-center' : ''}`}
              >
                <Icon className={iconClassName} />
                <span className={`${!isExpanded ? 'hidden' : 'font-medium'}`}>{link.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      <div className="mt-auto space-y-4 p-4">
        <div className={isExpanded ? 'w-full max-w-[calc(100%-2rem)] mx-auto' : 'flex justify-center'}>
          <div className="flex items-center w-full">
            {socialDot}
          </div>
        </div>
        <div className={isExpanded ? 'w-full max-w-[calc(100%-2rem)] mx-auto' : 'flex justify-center'}>
          <LanguageSelector isExpanded={isExpanded} />
        </div>
        <div className={isExpanded ? 'w-full mx-auto' : 'flex justify-center'}>
          <Button variant="outline" className={`${!isExpanded ? 'w-auto px-3' : 'w-full'}`} onClick={signOut}>
            <LogOut className={`h-5 w-5 ${isExpanded ? 'mr-2' : ''}`} />
            <span className={`${!isExpanded ? 'hidden' : ''}`}>{t('nav.signOut')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

